import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { AppService } from '@app/app.service';
import { ButtonComponent } from '@components/button/component/button.component';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, skip, takeUntil } from 'rxjs';

/** Компонент: строка - поиска */
@Component({
    selector: 'app-input-search',
    templateUrl: 'input-search.component.html',
    styleUrls: ['../styles/input-search.component.scss'],
    providers: [FuiDestroyService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSearchComponent implements OnInit {
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);
    readonly appService = inject(AppService);
    public form: FormGroup = new FormGroup({ searchStr: new FormControl(null) });

    /** Поток передачи данных поиска */
    searchStr$ = new BehaviorSubject<string>('');

    /** Текст поля поиска */
    @Input() public label: string;

    /** Позиция иконки */
    @Input() public positionIcon: 'left' | 'right' = 'left';

    /** Флаг отображения кнопок переключения */
    @Input() public showToggleArrowButtons: boolean = false;

    /** Иконка */
    @Input() public icon: string = 'filter-outline';

    /** Иконка */
    @Input() public disableDown: boolean = true;

    /** Иконка */
    @Input() public disableUp: boolean = true;

    /** Событие изменения состояния строки поиска */
    @Output() public readonly inputEvent = this.searchStr$.pipe(skip(1), debounceTime(1000), distinctUntilChanged());

    /** Событие изменения состояния строки поиска */
    @Output() public readonly searchEvent = new EventEmitter<string>();

    /** Событие изменения состояния строки поиска */
    @Output() public readonly switching = new EventEmitter<-1 | 1>();

    @ViewChild('arrowDownButton') arrowDownButton?: ButtonComponent;
    @ViewChild('arrowUpButton') arrowUpButton?: ButtonComponent;
    @ViewChild('searchInput') searchInput?: ElementRef<MatInput>;

    ngOnInit() {
        this.form
            .get('searchStr')
            .valueChanges.pipe(takeUntil(this.#destroy$))
            .subscribe((str) => {
                this.searchValue(str);
            });
    }

    /** Установка значения в поле поиска */
    public searchValue(value: string): void {
        const val = value ?? '';
        this.searchStr$.next(val);
        if (val === '') {
            this.searchByClick();
        }
    }

    /** Очистка формы */
    public clearForm(emitEvent = true): void {
        this.form.reset({ searchStr: '' }, { emitEvent });
        this.searchEvent.next('');
    }

    /** Поиск по Enter или по нажатию на кнопку поиска */
    public searchByClick() {
        this.searchEvent.next(this.searchStr$.value);
    }

    /** Перейти на следующий результат */
    arrowDownClick() {
        this.switching.emit(1);
        setTimeout(
            () => (this.arrowDownButton.disabled ? this.arrowUpButton.focus() : this.arrowDownButton.focus()),
            100,
        );
    }

    /** Перейти на предыдущий результат */
    arrowUpClick() {
        this.switching.emit(-1);
        setTimeout(
            () => (this.arrowUpButton.disabled ? this.arrowDownButton.focus() : this.arrowUpButton.focus()),
            100,
        );
    }

    /** Поместить фокус в поле поиска */
    focus() {
        this.searchInput?.nativeElement.focus();
    }
}
